import { useRef, useState } from "react";
import { useMutation } from "convex/react";
import { Loader2, Paperclip, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTyping } from "@/hooks/useTyping";

type PendingAttachment = {
  storageId: Id<"_storage">;
  name: string;
  contentType: string;
  size: number;
  previewUrl?: string;
};

export function MessageComposer({
  conversationId,
}: {
  conversationId: Id<"conversations">;
}) {
  const [body, setBody] = useState("");
  const [pending, setPending] = useState(false);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const send = useMutation(api.messages.send);
  const generateUploadUrl = useMutation(api.files.generateUploadUrl);
  const { ping, stopNow } = useTyping(conversationId);

  const submit = async () => {
    if (pending) return;
    const trimmed = body.trim();
    if (!trimmed && attachments.length === 0) return;
    setPending(true);
    try {
      await send({
        conversationId,
        body: trimmed,
        attachments: attachments.length
          ? attachments.map((a) => ({
              storageId: a.storageId,
              name: a.name,
              contentType: a.contentType,
              size: a.size,
            }))
          : undefined,
      });
      setBody("");
      setAttachments([]);
      stopNow();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  const upload = async (files: FileList) => {
    setUploading(true);
    try {
      const next: PendingAttachment[] = [];
      for (const file of Array.from(files)) {
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`${file.name} is larger than 25MB`);
          continue;
        }
        const url = await generateUploadUrl({});
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });
        if (!res.ok) {
          toast.error(`Upload failed for ${file.name}`);
          continue;
        }
        const { storageId } = (await res.json()) as {
          storageId: Id<"_storage">;
        };
        next.push({
          storageId,
          name: file.name,
          contentType: file.type,
          size: file.size,
          previewUrl: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : undefined,
        });
      }
      setAttachments((prev) => [...prev, ...next]);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-t p-3">
      {attachments.length > 0 ? (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((a, idx) => (
            <div
              key={a.storageId}
              className="relative rounded-md border bg-muted px-2 py-1 text-xs flex items-center gap-2"
            >
              {a.previewUrl ? (
                <img
                  src={a.previewUrl}
                  alt={a.name}
                  className="size-8 object-cover rounded"
                />
              ) : null}
              <span className="max-w-[180px] truncate">{a.name}</span>
              <button
                onClick={() =>
                  setAttachments((prev) => prev.filter((_, i) => i !== idx))
                }
                className="text-muted-foreground hover:text-foreground"
                aria-label="Remove"
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) {
              void upload(e.target.files);
              e.target.value = "";
            }
          }}
        />
        <Button
          variant="ghost"
          size="icon"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach"
        >
          {uploading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Paperclip className="size-4" />
          )}
        </Button>
        <Textarea
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            if (e.target.value) ping();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder="Type a message..."
          className="min-h-[44px] max-h-40 resize-none"
        />
        <Button
          onClick={submit}
          disabled={pending || (!body.trim() && attachments.length === 0)}
          size="icon"
        >
          {pending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Send className="size-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
